import { useEffect, useRef, useState } from "react";
import { FeedbackPanel } from "../../components/FeedbackPanel";

/**
 * Large sandboxed preview popup for `TemplateDetailScreen` — the inline
 * preview used to render straight into an unstyled iframe with no explicit
 * size, so it fell back to the browser default (~300x150px). This dialog
 * reuses the EXACT same `sandbox=""` posture (the rendered blob HTML is
 * still untrusted content, unchanged), just sized to fill most of the
 * viewport instead. Same Escape + boundary Tab-trap + focus-restoration
 * pattern as `ConfirmDialog`/`TemplateAttachmentsPanel`.
 */
export function TemplatePreviewDialog({
  html,
  onClose,
  title = "Vista previa",
  restoreFocusTo,
  stale = false,
  onRetry,
}: {
  html: string;
  onClose: () => void;
  /** REDISENO-PANEL.md §8.1/§8.2: "cabecera 'Vista previa' + nombre" — defaults to the bare title for a caller with no template name to append. */
  title?: string;
  /**
   * Native advisory (TemplatePreviewModal.tsx:~49): explicit override for the
   * self-captured `document.activeElement` below. `TemplatePreviewModal`
   * passes its OWN already-correct external-trigger capture here when it
   * replaces its own loading view with this dialog — capturing
   * `document.activeElement` fresh at THAT exact transition (the `undefined`
   * default path below) is unreliable: it runs during render, before the old
   * loading view's own (about to be removed) Cerrar button is actually gone
   * from the DOM, so it would grab that soon-to-be-detached node instead.
   * `undefined` (the default) preserves the original self-capture for any
   * other caller.
   */
  restoreFocusTo?: HTMLElement | null;
  /**
   * Codex review round 4 (P2, PR #387): the caller's own cached `html` can be
   * showing while a background revalidation of the SAME key has already
   * failed (`useQuery`'s own `stale: prev.data !== undefined` on a failed
   * refresh) — e.g. right after a list-level replace that never refreshed
   * this cache (`TemplatesScreen`'s "Vista previa"), or a plain remount-
   * triggered background refetch that fails. Without this, the operator had
   * no way to know the content on screen might already be out of date.
   * Same "Datos anteriores" copy/pattern `TemplateDetailScreen`'s own
   * preview card already uses for its inline (non-modal) preview.
   */
  stale?: boolean;
  onRetry?: () => void;
}) {
  const dialogRef = useRef<HTMLDivElement>(null);
  const closeButtonRef = useRef<HTMLButtonElement>(null);
  const previouslyFocusedRef = useRef<HTMLElement | null>(null);

  // Codex review, round 3: this used to restore focus unconditionally in the
  // effect's cleanup below, firing on EVERY unmount regardless of cause —
  // including the route-driven force-close `TemplateDetailScreen` performs
  // via its own `useEffect(() => setPreviewDialogOpen(false), [templateId])`
  // whenever the operator navigates (e.g. browser back/forward) to a
  // DIFFERENT active template while this dialog was open. That reset is a
  // state update inside an effect, so the resulting unmount-and-cleanup runs
  // in a LATER render pass than `RouteFocus`'s own post-navigation focus move
  // to the destination page's `<h1>` (app/RouteFocus.tsx) — the unconditional
  // restoration always won that race and yanked focus back onto the STILL-
  // RENDERED "Ver preview" trigger for the new template, silently defeating
  // route-change focus management for keyboard/screen-reader users.
  // Restoration now happens ONLY at the two explicit USER-initiated close
  // paths below (Escape, Cerrar click) — never as a blanket side effect of
  // unmounting for some OTHER reason the dialog itself has no visibility into.
  // Guards against the OWN focus-escape safety net below (see its comment)
  // firing on the very focus move THIS function performs: calling
  // `previouslyFocusedRef.current?.focus()` moves focus outside `dialogRef`
  // on purpose, and that safety net's job is to detect exactly that
  // shape of event and undo it — without this guard it would immediately
  // snap focus right back to "Cerrar", defeating the restoration in the
  // same tick it happens. Once a close has been requested, the trap has no
  // further job to do (the dialog is on its way out one way or another).
  const isClosingRef = useRef(false);
  // Design handoff v2: preview width toggle (Escritorio / Móvil). New UI —
  // purely presentational, the sandboxed iframe itself is unchanged.
  const [width, setWidth] = useState<"desktop" | "mobile">("desktop");

  const closeAndRestoreFocus = () => {
    isClosingRef.current = true;
    onClose();
    previouslyFocusedRef.current?.focus();
  };

  // Codex review, round 4: `main.tsx` mounts the app under `<StrictMode>`,
  // which in development deliberately double-invokes an effect's setup
  // (setup -> simulated cleanup -> setup again) to surface impure effects.
  // Capturing `document.activeElement` INSIDE the effect below used to be
  // that impurity: the FIRST setup correctly recorded the external "Ver
  // preview" trigger and focused Cerrar; the SECOND (StrictMode-replayed)
  // setup then captured `document.activeElement` again — but by then it was
  // already Cerrar (from the first setup's OWN focus call), overwriting the
  // real trigger with a self-reference. Capturing it here instead, during
  // RENDER rather than inside an effect, sidesteps the whole replay: React
  // only double-invokes EFFECTS in StrictMode, not the render body, and
  // nothing during render moves focus, so every render (replayed or not)
  // observes the SAME correct value. This is one of React's own documented
  // exceptions to "don't write refs during render" — initializing a ref
  // exactly once, guarded by a null check, is explicitly sanctioned.
  if (previouslyFocusedRef.current === null) {
    previouslyFocusedRef.current = restoreFocusTo !== undefined ? restoreFocusTo : (document.activeElement as HTMLElement | null);
  }

  useEffect(() => {
    closeButtonRef.current?.focus();

    const handleKeyDown = (event: KeyboardEvent) => {
      if (event.key === "Escape") {
        event.preventDefault();
        closeAndRestoreFocus();
        return;
      }

      if (event.key === "Tab" && dialogRef.current) {
        // Codex review on the popup migration PR: the preview iframe itself must be a
        // tab stop — without it, a keyboard user landing on "Cerrar" (the only match
        // before this fix) could never Tab forward into the iframe to navigate links or
        // scroll an interactive preview; every Tab just looped back to "Cerrar" itself.
        //
        // Unlike ConfirmDialog's boundary-only trap (which only intercepts wrap-around,
        // relying on the browser's own native Tab order for everything in between),
        // EVERY transition here is computed and applied explicitly. An <iframe> is not
        // reliably included in a browser/test environment's own "next tabbable element"
        // computation the way a <button> is — leaving the forward Cerrar->iframe hop to
        // native handling would make it invisible to jsdom and inconsistent across real
        // browsers. Explicit control sidesteps that entirely: this dialog only ever has
        // two focusable stops, so wrapping and adjacent-hop are the same operation.
        const focusable = Array.from(
          dialogRef.current.querySelectorAll<HTMLElement>(
            'button:not([disabled]), iframe, [tabindex]:not([tabindex="-1"])',
          ),
        );
        if (focusable.length === 0) return;
        event.preventDefault();
        const currentIndex = focusable.indexOf(document.activeElement as HTMLElement);
        const delta = event.shiftKey ? -1 : 1;
        const nextIndex = (currentIndex + delta + focusable.length) % focusable.length;
        focusable[nextIndex]!.focus();
      }
    };
    // Codex review, round 2: once focus moves INTO the iframe's own nested
    // document — by Tab past it, or by a direct click anywhere on the
    // rendered template content — keydown events fire in THAT document and
    // never bubble here, so Escape stops invoking onClose and the Tab-trap
    // above stops intercepting. This is a genuine platform limitation, not
    // an oversight: `sandbox=""` deliberately disables scripts inside, so
    // there is no channel (postMessage or otherwise) for that content to
    // notify this parent without reintroducing script execution — which
    // would defeat the whole reason the preview is sandboxed. What IS
    // reliably detectable here is focus escaping the ENTIRE dialog boundary
    // back out to the page behind it (a normal same-document focus change,
    // fired even when the iframe itself is what the browser tabbed out of)
    // — this listener pulls it straight back to "Cerrar" the instant that
    // happens, so a keyboard user can never end up back on the underlying
    // page while this modal is open. "Cerrar" itself stays reachable by
    // mouse/tap at all times regardless, for the narrower case where Escape
    // specifically doesn't fire because focus is still genuinely inside the
    // iframe's own content.
    const handleFocusIn = (event: FocusEvent) => {
      if (isClosingRef.current) return;
      if (dialogRef.current && event.target instanceof Node && !dialogRef.current.contains(event.target)) {
        closeButtonRef.current?.focus();
      }
    };

    document.addEventListener("keydown", handleKeyDown);
    document.addEventListener("focusin", handleFocusIn);
    return () => {
      document.removeEventListener("keydown", handleKeyDown);
      document.removeEventListener("focusin", handleFocusIn);
    };
    // onClose is a stable useCallback for this dialog's entire mounted
    // lifetime — mirrors ConfirmDialog's own justification for binding this
    // effect once.
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  return (
    <div className="v2-template-preview-dialog-backdrop">
      <div
        ref={dialogRef}
        className="v2-template-preview-dialog"
        role="dialog"
        aria-modal="true"
        aria-label={title}
      >
        <header className="v2-template-preview-dialog__header">
          <h2>{title}</h2>
          <div className="v2-template-preview-dialog__tools">
            <div role="group" aria-label="Ancho de vista" className="v2-segmented">
              <button type="button" aria-pressed={width === "desktop"} onClick={() => setWidth("desktop")}>
                Escritorio
              </button>
              <button type="button" aria-pressed={width === "mobile"} onClick={() => setWidth("mobile")}>
                Móvil
              </button>
            </div>
            <button
              type="button"
              className="v2-template-preview-dialog__close"
              aria-label="Cerrar"
              onClick={closeAndRestoreFocus}
              ref={closeButtonRef}
            >
              ×
            </button>
          </div>
        </header>
        <div className="v2-template-preview-dialog__stage">
          {stale && <FeedbackPanel variant="stale" message="Datos anteriores de preview." onRetry={onRetry} />}
          <iframe
            title="Preview de plantilla"
            sandbox=""
            srcDoc={html}
            className={`v2-template-preview-dialog__iframe v2-template-preview-dialog__iframe--${width}`}
          />
        </div>
        <footer className="v2-template-preview-dialog__footer">Render aislado: sin scripts ni acceso al mismo origen.</footer>
      </div>
    </div>
  );
}
